import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    fetchAvimarkDocumentSummaries,
    setAvimarkDocumentExportActive,
    type AvimarkDocumentSummary,
} from "@common/lib/api/avimarkEditor";
import { useAccountContext } from "@/state/providers/AccountProvider";

export const avimarkDocumentsKey = (accountId: string | undefined, ids: string[]) =>
    ["avimark-documents", accountId ?? "", [...ids].sort().join(",")] as const;

/** EN-1249: the hub's document list. Raw readiness and counts come from the server; hubDocumentRow maps them to a status. */
export function useAvimarkDocuments(documentTypeIds: string[], enabled: boolean) {
    const { accountData } = useAccountContext();
    const qc = useQueryClient();
    const key = avimarkDocumentsKey(accountData?.id, documentTypeIds);
    const [switchError, setSwitchError] = useState<string | null>(null);

    const query = useQuery({
        queryKey: key,
        queryFn: () => fetchAvimarkDocumentSummaries(documentTypeIds),
        enabled: enabled && !!accountData?.id && documentTypeIds.length > 0,
    });

    const mutation = useMutation({
        mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
            setAvimarkDocumentExportActive(id, isActive),
        onMutate: async ({ id, isActive }) => {
            setSwitchError(null);
            await qc.cancelQueries({ queryKey: key });
            const flip = (to: boolean) =>
                qc.setQueryData<AvimarkDocumentSummary[]>(key, (rows) =>
                    rows?.map((r) => (r.documentTypeId === id ? { ...r, isActive: to } : r)),
                );
            flip(isActive);
            return { undo: () => flip(!isActive) };
        },
        onError: (_e, _v, ctx) => {
            // Undo only this row, so a second switch flipped meanwhile keeps its new value.
            ctx?.undo();
            setSwitchError("Couldn't change this document. Try again.");
        },
        onSettled: () => void qc.invalidateQueries({ queryKey: key }),
    });

    const byId = useMemo(() => new Map((query.data ?? []).map((r) => [r.documentTypeId, r])), [query.data]);

    return {
        byId,
        isLoading: query.isLoading,
        isError: query.isError,
        refetch: () => void query.refetch(),
        setActive: (id: string, isActive: boolean) => mutation.mutate({ id, isActive }),
        switchError,
    };
}
